// Modal para gestionar archivos adjuntos de una homologación / acuerdo marco.
// PDFs, JPG, PNG — hasta 10 MB. Backend: /api/homologaciones/{id}/archivos.
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Paperclip, Upload, Download, Trash2, FileText, Loader2, X } from 'lucide-react';
import { homologacionesApi } from '../../services/api';
import { toast } from 'sonner';

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const HomologacionArchivosModal = ({ open, onOpenChange, homologacion, onChange }) => {
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef(null);

  const cargar = async () => {
    if (!homologacion?.id) return;
    setLoading(true);
    try {
      const r = await homologacionesApi.archivos(homologacion.id);
      setArchivos(r.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) cargar(); }, [open, homologacion?.id]);

  const onSubir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo supera 10 MB');
      e.target.value = '';
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('fichero', file);
      await homologacionesApi.subirArchivo(homologacion.id, fd);
      toast.success(`"${file.name}" subido`);
      await cargar();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const descargar = (a) => {
    // Usamos fetch con Authorization porque es endpoint autenticado
    fetch(homologacionesApi.descargarArchivo(a.id), {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error('Error descargando');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    }).catch((e) => toast.error(e.message));
  };

  const borrar = async (a) => {
    if (!confirm(`¿Borrar "${a.filename}"?`)) return;
    try {
      await homologacionesApi.borrarArchivo(a.id);
      toast.success('Archivo borrado');
      await cargar();
      if (onChange) onChange();
    } catch (e) { toast.error(e.message); }
  };

  if (!homologacion) return null;
  const tipoLabel = homologacion.tipo === 'acuerdo_marco_precios' ? 'acuerdo marco' : 'homologación';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dialog-homologacion-archivos">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-brand-500" />
            Archivos del {tipoLabel}
          </DialogTitle>
          <DialogDescription>
            {homologacion.entidad_nombre} · {homologacion.referencia_documento || 'sin referencia'}.
            Adjunta PDFs de acuerdos, certificados o justificantes (máx 10 MB por archivo).
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={onSubir}
            data-testid="hom-archivo-input"
          />
          <Button onClick={() => fileRef.current?.click()} disabled={subiendo} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-hom-subir-archivo">
            {subiendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {subiendo ? 'Subiendo…' : 'Subir archivo (PDF / JPG / PNG)'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-ink-500 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando archivos…</div>
        ) : archivos.length === 0 ? (
          <div className="text-center py-8 text-ink-400 border border-dashed border-ink-200 rounded-md" data-testid="hom-archivos-empty">
            <FileText className="w-6 h-6 mx-auto mb-1 text-ink-300" />
            Sin archivos adjuntos todavía.
          </div>
        ) : (
          <div className="border border-ink-200 rounded-md divide-y divide-ink-100" data-testid="hom-archivos-lista">
            {archivos.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 hover:bg-ink-50" data-testid={`hom-archivo-row-${a.id}`}>
                <FileText className="w-5 h-5 text-ink-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-800 truncate">{a.filename}</div>
                  <div className="text-xs text-ink-500">
                    {fmtSize(a.size_bytes)} · {a.content_type || '—'} ·
                    {' '}subido {a.uploaded_at?.slice(0, 10) || '—'} por {a.uploaded_by_email || '—'}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => descargar(a)} data-testid={`btn-hom-descargar-${a.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => borrar(a)} className="text-rose-600 hover:bg-rose-50" data-testid={`btn-hom-borrar-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-3.5 h-3.5 mr-1" /> Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HomologacionArchivosModal;
